/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import {
    Observable,
    ReplaySubject
} from 'rxjs';
import {take} from 'rxjs/operators';
import {AppService} from '../../app.service';
import {AuthenticationService} from '../authentication/authentication.service';
import {UserManagementService} from '../../api/ms-security/user-management/user-management.service';
import {UserInformationMapper} from './models/user-information.mapper';
import {UserInformationModel} from './models/user-information.model';
import {Permission} from '../utils/permission.enum';

@Injectable({
    providedIn: 'root'
})
export class UserService {
    public userServicesUrl = `${environment.securityBaseUrl}/api/user`;
    private userInformation: UserInformationModel;
    private loadedSubject = new ReplaySubject<void>(1);

    get onLoad(): Observable<void> {
        return this.loadedSubject.asObservable();
    }

    get isFirstAccess(): boolean {
        return this.userInformation?.permissions == null
                || this.userInformation?.permissions?.size === 0;
    }

    constructor(private http: HttpClient,
                private appService: AppService,
                private authService: AuthenticationService,
                private rolesService: UserManagementService
    ) {
        this.authService.onAuthentication.subscribe(() => {
            this.rolesService.reloadUser()
                    .pipe(take(1))
                    .subscribe((userInformation) => {
                        this.userInformation = UserInformationMapper.fromUserInformationContract(userInformation);
                        this.loadedSubject.next();
                        this.loadedSubject.complete();
                    });
        });
    }

    isMultiPartner(): boolean {
        return this.userInformation.multiPartner;
    }

    isMultiSupplyingPackingPlant(): boolean {
        return this.userInformation.multiSupplyingPackingPlant;
    }

    isMultiTechnicianGroup(): boolean {
        return this.userInformation.multiTechnician;
    }

    isMultiSupplier(): boolean {
        return this.userInformation.multiSupplier;
    }

    username(): string {
        return this.userInformation.username;
    }

    contexts(): string[] {
        return Array.from(this.userInformation?.permissions?.keys() ?? []);
    }

    hasContext(requiredContext: string): boolean {
        return this.contexts()
                .some((value) => requiredContext?.includes(value));
    }

    hasPermissions(requiredPermissions: string[], anyOrAll: 'all' | 'any' = 'any', requiredContext?: string): boolean {
        if (this.userInformation?.permissions == null) {
            return false;
        }

        if (requiredPermissions == null || requiredPermissions.length === 0) {
            return true;
        }

        if (requiredContext == null) {
            // This don't look at the context, so it looks to all permissions
            const permissions = Array.from(this.userInformation.permissions.values())
                    .flat();
            return this.permissionsHandler(permissions, requiredPermissions, anyOrAll);
        } else {
            if (this.userInformation.permissions.has(requiredContext)) {
                // It looks to the permissions of the given context
                const permissions = this.userInformation.permissions.get(requiredContext);
                return this.permissionsHandler(permissions, requiredPermissions, anyOrAll);
            }
            // The context has no permissions related to that context
            return false;
        }
    }

    permissionsHandler(permissions: Permission[], requiredPermissions: string[], anyOrAll: 'all' | 'any' = 'any'): boolean {
        switch (anyOrAll) {
            case 'all':
                return permissions.every((value) => requiredPermissions.includes(value));
            case 'any':
                return permissions.some((value) => requiredPermissions.includes(value));
            default:
                return false;
        }
    }

}
